import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Sparkles, Copy, Check, Heart } from "lucide-react";

export default function GiftPremium() {
  const { user } = useAuth() || {};
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ plan: "3mo", recipient_email: "", recipient_name: "", sender_name: "", personal_message: "Blessings to you — enjoy VOTLiving Premium!" });
  const [myGifts, setMyGifts] = useState([]);
  const [copied, setCopied] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/gifts/plans").then(r => setPlans(r.data.plans || [])); }, []);
  useEffect(() => { if (user) api.get("/me/gifts").then(r => setMyGifts(r.data)); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in first"); nav("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/gifts/purchase", { ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setLoading(false); }
  };

  const copy = (code) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(""), 1600); toast.success("Copied"); };

  return (
    <div className="pt-16" data-testid="gift-premium-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2 flex items-center gap-2"><Gift className="w-3 h-3"/>Gift a Membership</p>
        <h1 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">Bless Someone with Premium</h1>
        <p className="text-white/70 max-w-2xl mb-10">Give the gift of ad-free viewing, exclusive worship concerts, and downloadable devotional PDFs. Perfect for families, small groups, and ministry partners.</p>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-8">
          <div className="bg-[#0A0A0E] border border-[#D4AF37]/30 rounded-2xl p-8">
            <h3 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D4AF37]"/>Choose a Length</h3>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {plans.map(p => (
                <button key={p.key} onClick={()=>setForm({...form, plan: p.key})} className={`p-4 rounded-xl border-2 text-left transition-all ${form.plan === p.key ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/25 bg-white/[0.03]"}`} data-testid={`gift-plan-${p.key}`}>
                  <p className="font-heading text-lg font-bold text-white">{p.name}</p>
                  <p className="font-heading text-2xl font-bold text-[#D4AF37] mt-1">${p.price_usd.toFixed(p.key === "12mo" ? 0 : 2)}</p>
                  {p.savings && <p className="text-[10px] tracking-widest uppercase text-[#D4AF37]/80 mt-1">{p.savings}</p>}
                </button>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-3">
              <Field placeholder="Recipient email" type="email" value={form.recipient_email} onChange={v=>setForm({...form,recipient_email:v})} testid="gift-recipient-email" required/>
              <Field placeholder="Recipient name (optional)" value={form.recipient_name} onChange={v=>setForm({...form,recipient_name:v})} testid="gift-recipient-name"/>
              <Field placeholder="Your name (optional)" value={form.sender_name} onChange={v=>setForm({...form,sender_name:v})} testid="gift-sender-name"/>
              <textarea rows={3} placeholder="Personal message" value={form.personal_message} onChange={e=>setForm({...form,personal_message:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="gift-message"/>
              <button disabled={loading} className="w-full btn-gold py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="gift-submit">
                <Heart className="w-4 h-4"/>{loading ? "Redirecting…" : "Purchase Gift"}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold mb-4">Your Gift Codes</h3>
              {!user ? <p className="text-white/60 text-sm">Sign in to view your gifts.</p>
                : myGifts.length === 0 ? <p className="text-white/60 text-sm" data-testid="no-gifts">You haven't purchased any gifts yet.</p>
                : (
                  <div className="space-y-3">
                    {myGifts.map(g => (
                      <div key={g.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-4" data-testid={`gift-${g.id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <p className="font-heading font-semibold text-white">{g.months} months → {g.recipient_email}</p>
                            <p className="text-xs text-white/50">{g.status === "paid" ? "Ready to share" : g.status === "redeemed" ? `Redeemed by ${g.redeemed_by_user_id}` : "Pending payment"}</p>
                          </div>
                          <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded ${g.status === "paid" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : g.status === "redeemed" ? "bg-white/10 text-white/60" : "bg-yellow-400/10 text-yellow-400"}`}>{g.status}</span>
                        </div>
                        {g.status === "paid" && (
                          <div className="mt-3 flex items-center gap-2 bg-black/40 rounded-lg px-3 py-2 border border-[#D4AF37]/20">
                            <code className="font-heading text-lg tracking-widest text-[#D4AF37] flex-1">{g.code}</code>
                            <button onClick={()=>copy(g.code)} className="btn-ghost rounded-full p-2" data-testid={`copy-gift-${g.id}`}>{copied === g.code ? <Check className="w-4 h-4 text-[#D4AF37]"/> : <Copy className="w-4 h-4"/>}</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>

            <div className="bg-gradient-to-r from-[#0F0F14] to-[#0A0A0E] border border-white/10 rounded-2xl p-6">
              <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-2">Received a Gift?</p>
              <h4 className="font-heading text-xl font-bold mb-3">Redeem your code</h4>
              <p className="text-white/60 text-sm mb-4">If someone gifted you a Premium membership, enter your code to activate it.</p>
              <Link to="/redeem-gift" className="inline-block btn-gold rounded-full px-5 py-2 text-sm font-semibold" data-testid="go-redeem">Redeem now →</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ testid, ...props }) {
  return <input {...props} onChange={e=>props.onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid={testid}/>;
}
